import {
  FluidBadgeConfig,
  FluidColor,
  FluidIconConfig,
  FluidTheme,
  FluidThemeInterface,
} from '@lmig/fluid-core';
import {
  Component,
  ComponentInterface,
  Element,
  h,
  Host,
  Prop,
  State,
  Watch,
} from '@stencil/core';

import { useI18n } from '../../providers/fluid-i18n-provider/adapter/fluid-i18n.adapter';
import { FluidI18nStore } from '../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import { Utils } from '../../utils/utils';
import {
  BadgeIcon,
  BadgeIconSlot,
  BadgeLabel,
  FluidBadgeTranslationOptions,
} from './components/fluid-badge.components';

/**
 * @displayName Badge
 *
 * @contextData FLUID Badge is simply a label that holds a small amount of contextual information. It is commonly used for displaying status words & phrases (e.g. "New", "Pending", "Success", "UW Review Needed", etc.), dates, numerical values, etc. Badge should be displayed in close proximity to the information that it's associated with, and should have a color that indicates the tone or severity of its label.
 *
 * @slot icon-before - Named Slot for icon displayed before Badge label. Only available if "iconBefore" Prop is NOT specified.
 * @slot icon-after - Named Slot for icon displayed after Badge label. Only available if "iconAfter" Prop is NOT specified.
 *
 * @builder true
 *
 */
@Component({
  tag: 'fluid-badge',
  styleUrl: 'fluid-badge.less',
  shadow: true,
})
export class FluidBadge implements ComponentInterface, FluidThemeInterface {
  // ================================================================ //
  // -- Host Element

  /**
   * Get a reference to the host element
   */
  @Element() host: HTMLFluidBadgeElement;

  // ================================================================ //
  // -- Internationalisation

  /* @internal */
  @Prop({ mutable: true }) fluidTranslate: FluidBadgeTranslationOptions;
  private _translateId: string;
  @State() private _i18nStore: FluidI18nStore | undefined;

  // ================================================================ //
  // -- Props

  /**
   * What theme settings should the component use?
   */
  @Prop({ mutable: true }) theme: FluidTheme = FluidTheme.CORP;

  /**
   * If FluidThemeProvider is used, should this component override the
   * global theme with its local theme?
   */
  @Prop({ mutable: true }) overrideGlobalTheme: boolean;

  /**
   * Text to be displayed within the Badge body.
   */
  @Prop({ mutable: true }) label: string;

  /**
   * If true, Badge will have a solid-color background. Otherwise, Badge
   * will have transparent background with color border & label. This Prop
   * can be used in combination with "chip" & "indicator" Props.
   */
  @Prop({ mutable: true }) solid = false;

  /**
   * Default setting is true, only legacy theme will have this set to false. If false, badge will display with rectangular styling.
   */
  @Prop({ mutable: true }) chip = true;

  /**
   * If true, Badge will be styled as an "indicator" for displaying a
   * numerical value, character marker, or status word (e.g. a number within a
   * Tree Menu menu item). NOTE: Badge should NOT be used as a standalone
   * component when this Prop is utilized. Indicator Badges should only be
   * used as "add-ons" to indicate status or context, and should be displayed
   * beside label text or in the upper right-hand corner of a container.
   */
  @Prop({ mutable: true }) indicator = false;

  /**
   * If provided, renders a FLUID Icon to the left of the Badge label.
   */
  @Prop({ mutable: true }) iconBefore: FluidIconConfig;

  /**
   * If provided, renders a FLUID Icon to the right of the Badge label.
   */
  @Prop({ mutable: true }) iconAfter: FluidIconConfig;

  /**
   * Primary color used for Badge border, label (when "solid" Prop is false),
   * & background (when "solid" Prop is true).
   */
  @Prop({ mutable: true }) color: Exclude<
    FluidColor,
    FluidColor.RED_ORANGE | FluidColor.DEEP_YELLOW | FluidColor.OPAQUE
  > = FluidColor.INFO;

  /**
   * If true, Badge will be rendered as a block-level element instead of
   * inline-block.
   */
  @Prop({ mutable: true }) block = false;

  /**
   * Allows Badge @Props to be set via a single configuration object.
   */
  @Prop({ mutable: true }) config: FluidBadgeConfig;

  @Watch('config')
  onConfigChange() {
    Utils.selectiveMapConfigToLocal(this, this.config);
  }

  // ================================================================ //
  // -- Lifecycle Hooks

  /**
   * Called every time the component is connected to the DOM.
   * @action: setGlobalTheme - updates theme to match global theme & subscribes to changes
   */
  connectedCallback(): void {
    Utils.selectiveMapConfigToLocal(this, this.config);
    useI18n.bind(this)();
    Utils.setGlobalTheme(this);
  }

  /**
   * Called every time the component is disconnected from the DOM.
   * @action deregisterElement - removes the element from the i18n Store registry
   */
  disconnectedCallback() {
    this._i18nStore?.deregisterElement(this._translateId);
  }

  // ================================================================ //
  // -- Render (Partials)

  /**
   * If "iconBefore"/"iconAfter" Prop(s) used, renders appropriate FLUID Icon.
   * Otherwise, render Icon Slot if slotted element(s) provided.
   * @param config - FluidIconConfig object from "iconBefore" / "iconAfter" Prop
   * @param position - "before"/"after" string used for CSS class name
   */
  renderIcon(config: FluidIconConfig, position: 'before' | 'after') {
    if (config) {
      return (
        <BadgeIcon config={config} position={position} theme={this.theme} />
      );
    } else {
      return <BadgeIconSlot host={this.host} position={position} />;
    }
  }

  // ================================================================ //
  // -- Render (Main)

  render() {
    return (
      <Host
        class={{
          'badge-layout-block': this.block,
        }}>
        <div
          class={{
            [`fluid-badge ${this.theme}`]: true,
            [`badge-color-${this.color}`]: true,
            'badge-color-solid': this.solid,
            'badge-style-chip': this.theme !== 'legacy' && this.chip,
            'badge-style-indicator': this.indicator,
            'badge-layout-block': this.block,
          }}>
          {this.renderIcon(this.iconBefore, 'before')}
          <BadgeLabel
            fluidTranslate={this.fluidTranslate}
            label={this.label}
            i18nStore={this._i18nStore}
          />
          {this.renderIcon(this.iconAfter, 'after')}
        </div>
      </Host>
    );
  }
}
